import type { RetentionSettingsUpdateFailedAction } from '@Application/Accounts/RetentionSettings/Actions/retentionSettingsUpdateFailed';
import UpdateRetentionSettings from '@Application/Accounts/RetentionSettings/Commands/UpdateRetentionSettings';
import updateRetentionSettings from '@Application/Accounts/RetentionSettings/updateRetentionSettings';
import DurationChoice, {
	type DurationPreset,
} from '@UI/Pages/Administration/Retention/RetentionSettingsForm/DurationChoice';
import retentionBounds from '@UI/Pages/Administration/Retention/RetentionSettingsForm/Helpers/retentionBounds';
import RetentionReductionDialog from '@UI/Pages/Administration/Retention/RetentionSettingsForm/ConfirmationDialogs/RetentionReductionDialog';
import RetentionSettingsFormData from '@UI/Pages/Administration/Retention/RetentionSettingsForm/FormData/RetentionSettingsFormData';
import type RetentionSettingsView from '@UI/Store/RetentionSettings/Views/RetentionSettingsView';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch } from '@verclam/synapse';
import { Button, Form } from '@verclam/ui-kit';

const formStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.75rem;
`;

const audioPresets: readonly DurationPreset[] = [
	{ label: '30 jours', value: 30 },
	{ label: '90 jours', value: 90 },
	{ label: '6 mois', value: 180 },
	{ label: '1 an', value: 365 },
];

const beneficiaryPresets: readonly DurationPreset[] = [
	{ label: '2 ans', value: 24 },
	{ label: '5 ans', value: 60 },
	{ label: '10 ans', value: 120 },
];

type Props = {
	readonly settings: RetentionSettingsView;
};

/** Edits the two retention durations; shortening either one goes through a strengthened confirmation. */
const RetentionSettingsForm: React.FC<Props> = ({ settings }) => {
	const dispatch = useDispatch();
	const formId = React.useId();

	const [formData, setFormData] = React.useState(() => RetentionSettingsFormData.fromView(settings));
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [isReductionDialogVisible, setIsReductionDialogVisible] = React.useState(false);

	const handleAudioChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setAudioRetentionDays(value));
	}, []);

	const handleBeneficiaryChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setBeneficiaryRetentionMonths(value));
	}, []);

	const performUpdate = React.useCallback(() => {
		const durations = formData.toCommandData();
		if (durations === null) {
			return;
		}

		setIsSubmitting(true);

		dispatch(
			updateRetentionSettings(
				new UpdateRetentionSettings({ accountId: settings.accountId, ...durations }),
			),
		)
			.catch((action: RetentionSettingsUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'RETENTION_SETTINGS_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [dispatch, formData, settings.accountId]);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		if (formData.isReductionOf(settings)) {
			setIsReductionDialogVisible(true);
			return;
		}

		performUpdate();
	}, [formData, settings, performUpdate]);

	const handleReductionDialogHide = React.useCallback(() => {
		setIsReductionDialogVisible(false);
	}, []);

	const handleReductionAccept = React.useCallback(() => {
		setIsReductionDialogVisible(false);
		performUpdate();
	}, [performUpdate]);

	return (
		<React.Fragment>
			<Form id={formId} onSubmit={handleSubmit}>
				<div css={formStyle}>
					<DurationChoice
						name="audioRetentionDays"
						title="Enregistrement audio"
						description="Après purge, la citation, le locuteur et l'horodatage restent dans le document. Seule la réécoute disparaît."
						presets={audioPresets}
						unit="jours"
						minimum={retentionBounds.audioDays.minimum}
						maximum={retentionBounds.audioDays.maximum}
						value={formData.audioRetentionDays}
						onChange={handleAudioChange}
						errorMessage={formData.getValidationMessage('audioRetentionDays')}
						isValid={formData.isValid('audioRetentionDays')}
					/>
					<DurationChoice
						name="beneficiaryRetentionMonths"
						title="Dossier archivé"
						description="Cette durée ne court qu'à partir de l'archivage et vaut pour tous les dossiers archivés, quel que soit leur type — entretiens, transcriptions, documents, versions comprises. Un dossier actif n'est jamais purgé. Le dossier est signalé un mois avant sa suppression."
						presets={beneficiaryPresets}
						unit="mois"
						minimum={retentionBounds.beneficiaryMonths.minimum}
						maximum={retentionBounds.beneficiaryMonths.maximum}
						value={formData.beneficiaryRetentionMonths}
						onChange={handleBeneficiaryChange}
						errorMessage={formData.getValidationMessage('beneficiaryRetentionMonths')}
						isValid={formData.isValid('beneficiaryRetentionMonths')}
					/>
					<div>
						<Button type="submit" label="Enregistrer" loading={isSubmitting} />
					</div>
				</div>
			</Form>

			<RetentionReductionDialog
				visible={isReductionDialogVisible}
				accountName={settings.accountName}
				onHide={handleReductionDialogHide}
				onAccept={handleReductionAccept}
			/>
		</React.Fragment>
	);
};

export default RetentionSettingsForm;
